import React from 'react';
import { SideNavigation, SideNavigationProps } from '@amzn/awsui-components-react';
import { externalLinks } from '../../data/externalLinks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { publishKatalMetric } from '../katalAnalytics';
import {
  ACTIVITY_PAGE,
  ADMIN_EVENT_PAGE_ACCESS,
  COORDINATOR_ACTIVITY_DASHBOARD_LINK,
  EVENT_CREATION,
} from '../../utils/constants';
import { featureIsEnabled, useFeatures } from '../../hooks/features';

interface ActivitySidebarProps {
  baseUrl: string;
}

export function ActivitySidebar(props: ActivitySidebarProps) {
  const { t } = useTranslation(['activity', 'translation']);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  const urlFeatures = searchParams.get('features');
  const volunteerActivityUrl = `/my-activity${urlFeatures ? '?features=' + urlFeatures : ''}`;
  const coordinatorActivityUrl = `/coordinator-activity${urlFeatures ? '?features=' + urlFeatures : ''}`;

  const [activeHref, setActiveHref] = useState(props.baseUrl);

  const volunteerActivitySection: SideNavigationProps.Item = featureIsEnabled(
    EVENT_CREATION,
    features,
    searchParams
  )
    ? {
        type: 'section',
        text: t('sideNavigation.volunteerActivity'),
        items: [
          {
            type: 'link',
            text: t('upcoming'),
            href: `${volunteerActivityUrl}${urlFeatures ? '&' : '?'}tab=upcoming`,
          },
          {
            type: 'link',
            text: t('past'),
            href: `${volunteerActivityUrl}${urlFeatures ? '&' : '?'}tab=past`,
          },
        ],
      }
    : {
        type: 'link',
        text: t('sideNavigation.volunteerActivity'),
        href: volunteerActivityUrl,
      };

  const coordinatorActivitySection: SideNavigationProps.Item = featureIsEnabled(
    EVENT_CREATION,
    features,
    searchParams
  )
    ? {
        type: 'section',
        text: t('sideNavigation.coordinatorActivity'),
        items: [
          {
            type: 'link',
            text: t('upcoming'),
            href: `${coordinatorActivityUrl}${urlFeatures ? '&' : '?'}tab=upcoming`,
          },
          {
            type: 'link',
            text: t('past'),
            href: `${coordinatorActivityUrl}${urlFeatures ? '&' : '?'}tab=past`,
          },
          {
            type: 'link',
            text: t('draft'),
            href: `${coordinatorActivityUrl}${urlFeatures ? '&' : '?'}tab=draft`,
          },
        ],
      }
    : {
        type: 'link',
        text: t('sideNavigation.coordinatorActivity'),
        href: coordinatorActivityUrl,
      };

  const resourceItems: SideNavigationProps.Link[] = [
    {
      type: 'link',
      text: t('sideNavigation.WikiAmazonVolunteering'),
      href: externalLinks.myActivity.wiki.amazonVolunteering,
      external: true,
    },
    {
      type: 'link',
      text: t('sideNavigation.wikiManageEvent'),
      href: externalLinks.myActivity.wiki.manageEvent,
      external: true,
    },
    {
      type: 'link',
      text: t('sideNavigation.WikiPlaybooks'),
      href: externalLinks.myActivity.wiki.playbooks,
      external: true,
    },
    {
      type: 'link',
      text: t('sideNavigation.GiveHubCharitableOrgs'),
      href: externalLinks.myActivity.givehub.charitableOrgs,
      external: true,
    },
  ];

  if (featureIsEnabled(ADMIN_EVENT_PAGE_ACCESS, features, searchParams)) {
    resourceItems.push({
      type: 'link',
      text: t('sideNavigation.ACIVDashboardEventSearch'),
      href: externalLinks.myActivity.dashboard.eventSearch,
      external: true,
    });
  }

  return (
    <SideNavigation
      activeHref={activeHref}
      header={{
        href: '#/',
        text: t('sideNavigation.title'),
      }}
      onFollow={(event) => {
        if (!event.detail.external) {
          event.preventDefault();
          setActiveHref(event.detail.href);
          navigate(event.detail.href);
        } else {
          if (event.detail.href === externalLinks.myActivity.dashboard.eventSearch) {
            publishKatalMetric(COORDINATOR_ACTIVITY_DASHBOARD_LINK, event.detail.href);
          }
          publishKatalMetric(ACTIVITY_PAGE + '_externalLink', event.detail.href);
        }
      }}
      items={[
        volunteerActivitySection,
        coordinatorActivitySection,
        { type: 'divider' },
        {
          type: 'section',
          text: t('sideNavigation.resources'),
          items: resourceItems,
        },
      ]}
    />
  );
}
